import React, { useEffect, useState } from "react";
import axiosInstance from "../../../components/api/axiosInstance";
import { getAuthItem } from "../../../utils/authStorage";
import { errorMessage } from "../dmApi";

const NEW_OWNER = "__new__";
const DESCRIPTION_MAX = 500;

function currentUserId() {
  try {
    const user = JSON.parse(getAuthItem("user") || "null");
    return user?.user_id ?? user?.id ?? null;
  } catch {
    return null;
  }
}

const listFrom = (res) => (Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : []);

/**
 * Staff: add a service offered by a service based client (store, restaurant, studio, agency…).
 * Services belong to a tenant client (the brand / branch); one can be created inline.
 */
function ClientServiceForm({ tenantId, tenantName, onSaved, onCancel }) {
  const [owners, setOwners] = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(true);
  const [ownerId, setOwnerId] = useState("");
  const [ownerName, setOwnerName] = useState(tenantName || "");
  const [serviceName, setServiceName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get("/tenant-clients/", { params: { tenant_id: tenantId } })
      .then((res) => {
        if (cancelled) return;
        const rows = listFrom(res)
          .filter((r) => String(r.Status || "Active").toLowerCase() !== "inactive")
          .map((r) => ({ id: String(r.Tenant_Client_Id), name: r.Client_Name || `Brand #${r.Tenant_Client_Id}` }));
        setOwners(rows);
        setOwnerId(rows.length === 1 ? rows[0].id : rows.length ? "" : NEW_OWNER);
      })
      .catch(() => !cancelled && setOwnerId(NEW_OWNER))
      .finally(() => !cancelled && setLoadingOwners(false));
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!ownerId) return setError("Select the brand / branch.");
    if (ownerId === NEW_OWNER && !ownerName.trim()) return setError("Enter the brand / branch name.");
    if (!serviceName.trim()) return setError("Service name is required.");

    const userId = currentUserId();
    setSaving(true);
    try {
      let clientId = ownerId;
      if (ownerId === NEW_OWNER) {
        const res = await axiosInstance.post("/tenant-clients/", {
          Tenant_Id: tenantId,
          Client_Name: ownerName.trim(),
          Created_By: userId,
          Updated_By: userId,
        });
        clientId = res.data?.data?.Tenant_Client_Id;
        if (!clientId) throw new Error("Could not create the brand / branch.");
        setOwners((list) => [...list, { id: String(clientId), name: ownerName.trim() }]);
        setOwnerId(String(clientId));
      }
      await axiosInstance.post("/tenant-client-services/", {
        Tenant_Id: tenantId,
        Tenant_Client_Id: clientId,
        Service_Name: serviceName.trim(),
        Description: description.trim() || null,
        Created_By: userId,
        Updated_By: userId,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || errorMessage(err, "Could not save service"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="dmp-card dmc-project-form" onSubmit={submit}>
      <div className="dmc-project-form__head">
        <h3 className="dmp-card__title">New service{tenantName ? ` for ${tenantName}` : ""}</h3>
      </div>
      {error ? <div className="dmp-error">{error}</div> : null}
      <div className="dmp-form-grid">
        <label className="dmp-field">
          Brand / Branch *
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} disabled={loadingOwners}>
            {loadingOwners ? <option value="">Loading…</option> : null}
            {!loadingOwners && owners.length > 1 ? <option value="">Select brand</option> : null}
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
            {!loadingOwners ? <option value={NEW_OWNER}>+ New brand / branch</option> : null}
          </select>
        </label>
        {ownerId === NEW_OWNER ? (
          <label className="dmp-field">
            Brand / Branch Name *
            <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="e.g. Main store" />
          </label>
        ) : null}
        <label className="dmp-field">
          Service Name *
          <input
            value={serviceName}
            onChange={(e) => setServiceName(e.target.value)}
            placeholder="e.g. Home delivery, Bridal shoot, Rentals"
            maxLength={150}
            autoFocus
          />
        </label>
        <label className="dmp-field dmp-field--full">
          Description
          <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={DESCRIPTION_MAX} />
        </label>
      </div>
      <div className="dmc-project-form__actions">
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="dmp-btn" disabled={saving}>
          {saving ? "Saving…" : "Save service"}
        </button>
      </div>
    </form>
  );
}

export default ClientServiceForm;
